import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Modal,
} from '@mui/material';
import React, { useState, useEffect } from 'react';
import CustomCheckbox from '../CustomCheckbox';
import CloseIcon from '@mui/icons-material/Close';
import { FormInputText } from '../../form-components/FormInputText';
import { useForm, Controller } from 'react-hook-form';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckIcon from '@mui/icons-material/Check';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { useSelector } from 'react-redux';
import { useToast } from 'use-toast-mui';
import { errorMessages } from '../../utils/methods';
import {
  getEmployeePositionsList,
  createEmployeePosition,
  updateEmployeePosition,
  deleteEmployeePosition,
  getPositionEmployeeCount,
} from '../../api/employeePosition';

const TEAL = '#4AB3BC';
const TEAL_DARK = '#3a9da5';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '606px',
  maxHeight: '80vh',
  bgcolor: '#fff',
  borderRadius: '10px',
  border: 'none',
  display: 'flex',
  flexDirection: 'column',
};

const initialFormValues = {
  employeePositionName: '',
  employeePositionOrder: '',
};

var qs = require('qs');

export default function EmployeePosition({ setOpens, opens, onUpdate }) {
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [key, setKey] = useState(0);
  const [editingRow, setEditingRow] = useState(null); // 인라인 수정 중인 행
  const [editName, setEditName] = useState('');
  const [editOrder, setEditOrder] = useState('');
  const [confirmModal, setConfirmModal] = useState({ open: false, message: '', onConfirm: null, employees: [], type: 'delete' });

  const toast = useToast();
  const token = useSelector(state => state.user.user.token);
  const userBranch = useSelector(state => state.user.user.branch);
  const userHead = useSelector(state => state.user.user.headBranch);

  const { control, reset, getValues } = useForm({ defaultValues: initialFormValues });

  const fetchPositions = async () => {
    try {
      setLoading(true);
      const queryParams = { headBranchId: userHead?.headBranchId, branchId: userBranch?.branchId };
      const result = await getEmployeePositionsList(token, qs.stringify(queryParams));
      setPositions((result.content || []).map(p => ({ ...p, isChecked: false })));
    } catch (error) {
      toast.error(errorMessages(error || '직급 목록을 불러오는데 실패했습니다'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (opens?.openPosition) {
      resetForm();
      fetchPositions();
    }
  }, [opens?.openPosition]);

  const resetForm = () => {
    reset(initialFormValues);
    setEditingRow(null);
    setKey(prev => prev + 1);
  };

  const handleChange = e => {
    const { name, checked } = e.target;
    if (name === 'allSelect') {
      setPositions(prev => prev.map(p => ({ ...p, isChecked: checked })));
    } else {
      setPositions(prev => prev.map(p =>
        p.employeePositionName + p.employeePositionId === name ? { ...p, isChecked: checked } : p,
      ));
    }
  };

  // 순서 입력 필터 (숫자만, 0만 입력 불가)
  const filterOrderInput = value => value.replace(/[^0-9]/g, '').replace(/^0+$/, '');

  // 순서 중복 체크
  const isOrderDuplicate = (order, excludeId) => {
    return positions.some(p => p.employeePositionOrder === order && p.employeePositionId !== excludeId);
  };

  // 직급 추가
  const handleSubmitPosition = async () => {
    const formValues = getValues();
    const name = formValues.employeePositionName?.trim();
    const order = parseInt(formValues.employeePositionOrder);

    if (!name || !formValues.employeePositionOrder) {
      toast.error('직급명과 직급 순서를 모두 입력해주세요');
      return;
    }
    if (order <= 0 || isNaN(order)) {
      toast.error('직급 순서는 1 이상의 숫자여야 합니다');
      return;
    }
    if (isOrderDuplicate(order, null)) {
      toast.error('직급 순서는 중복이 불가능합니다');
      return;
    }

    try {
      const data = new FormData();
      data.append('employeePositionName', name);
      data.append('employeePositionOrder', order);
      data.append('headBranchId', userHead?.headBranchId);
      data.append('branchId', userBranch?.branchId);
      await createEmployeePosition(data);
      toast.success('직급이 추가되었습니다');
      resetForm();
      fetchPositions();
    } catch (error) {
      toast.error(errorMessages(error || '직급 추가에 실패했습니다'));
    }
  };

  // 인라인 수정 시작
  const startEdit = row => {
    setEditingRow(row.employeePositionId);
    setEditName(row.positionName || row.employeePositionName);
    setEditOrder(String(row.order || row.employeePositionOrder));
  };

  // 인라인 수정 저장
  const saveEdit = () => {
    const name = editName.trim();
    const order = parseInt(editOrder);
    if (!name || !editOrder) { toast.error('직급명과 순서를 입력해주세요'); return; }
    if (order <= 0 || isNaN(order)) { toast.error('직급 순서는 1 이상이어야 합니다'); return; }
    if (isOrderDuplicate(order, editingRow)) {
      toast.error('직급 순서는 중복이 불가능합니다');
      return;
    }

    setConfirmModal({
      open: true,
      message: `"${name}" 직급을 수정하시겠습니까?`,
      type: 'edit',
      totalEmp: 0,
      employees: [],
      onConfirm: async () => {
        try {
          const data = new FormData();
          data.append('employeePositionId', editingRow);
          data.append('employeePositionName', name);
          data.append('employeePositionOrder', order);
          data.append('headBranchId', userHead?.headBranchId);
          data.append('branchId', userBranch?.branchId);
          await updateEmployeePosition(data);
          toast.success('직급이 수정되었습니다');
          setEditingRow(null);
          fetchPositions();
          onUpdate?.();
        } catch (error) {
          toast.error(errorMessages(error || '직급 수정에 실패했습니다'));
        }
        setConfirmModal({ open: false });
      },
    });
  };

  // 삭제 공통 (직원 수 조회 → 안내문 → 삭제)
  const handleDelete = async (ids, label) => {
    try {
      const result = await getPositionEmployeeCount(token, ids.join(','));
      const counts = result.counts || [];
      const employees = result.employees || [];
      const totalEmp = counts.reduce((sum, c) => sum + (Number(c.employeeCount) || 0), 0);

      setConfirmModal({
        open: true,
        message: `${label} 삭제하시겠습니까?`,
        totalEmp,
        employees,
        onConfirm: async () => {
          try {
            const data = new FormData();
            data.append('selectedId', ids.join(','));
            await deleteEmployeePosition(data, token);
            toast.success('직급이 삭제되었습니다');
            fetchPositions();
            onUpdate?.();
          } catch (error) {
            toast.error(errorMessages(error));
          }
          setConfirmModal({ open: false, employees: [] });
        },
      });
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  // 개별 삭제
  const handleDeleteSingle = row => {
    const name = row.positionName || row.employeePositionName;
    handleDelete([row.employeePositionId], `"${name}" 직급을`);
  };

  // 선택 삭제
  const handleDeleteSelected = () => {
    const selected = positions.filter(p => p.isChecked);
    if (selected.length === 0) { toast.error('삭제할 직급을 선택해주세요'); return; }
    handleDelete(selected.map(p => p.employeePositionId), `선택한 ${selected.length}개 직급을`);
  };

  return (
    <>
      <Modal
        open={opens?.openPosition || false}
        onClose={() => setOpens({ ...opens, openPosition: false })}>
        <Box sx={{ ...style }}>
          {/* 헤더 */}
          <Box sx={{
            backgroundColor: '#111', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            height: '52px', p: '0 20px', borderTopRightRadius: '10px', borderTopLeftRadius: '10px', flexShrink: 0,
          }}>
            <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '15px' }}>직급 관리</Typography>
            <CloseIcon onClick={() => setOpens({ ...opens, openPosition: false })} sx={{ color: '#fff', cursor: 'pointer' }} />
          </Box>

          {/* 입력 영역 (고정) */}
          <Box sx={{ p: '24px 28px 0', flexShrink: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography sx={{ fontWeight: 500, minWidth: '80px', fontSize: '14px' }}>직급명</Typography>
              <FormInputText
                key={`employeePositionName-${key}`}
                control={control}
                name="employeePositionName"
                sx={{ flex: 1 }}
                placeholder="직급명을 입력하세요"
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography sx={{ fontWeight: 500, minWidth: '80px', fontSize: '14px' }}>직급 순서</Typography>
              <Controller
                key={`employeePositionOrder-${key}`}
                control={control}
                name="employeePositionOrder"
                render={({ field }) => (
                  <TextField
                    {...field}
                    size="small"
                    sx={{ flex: 1 }}
                    placeholder="순서를 입력하세요 (1 이상)"
                    inputProps={{ inputMode: 'numeric' }}
                    onChange={e => {
                      const v = e.target.value.replace(/[^0-9]/g, '').replace(/^0+/, '');
                      field.onChange(v);
                    }}
                  />
                )}
              />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
              <Button size="small" onClick={resetForm} sx={{
                p: '5px 20px', fontSize: '13px', height: '36px',
                color: '#fff', background: '#9A9A9A', borderRadius: '8px',
                '&:hover': { backgroundColor: '#888' },
              }}>초기화</Button>
              <Button size="small" onClick={handleSubmitPosition} disabled={loading} sx={{
                p: '5px 20px', fontSize: '13px', height: '36px',
                color: '#fff', background: TEAL, borderRadius: '8px',
                '&:hover': { backgroundColor: TEAL_DARK },
              }}>직급 추가</Button>
            </Box>
            <Box sx={{ borderTop: '1px solid #e8e8e8', mb: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography sx={{ fontWeight: 600, fontSize: '15px' }}>
                직급 현황 <span style={{ color: TEAL, fontWeight: 700 }}>{positions?.length || 0}</span>개
              </Typography>
              {positions.some(p => p.isChecked) && (
                <Button size="small" onClick={handleDeleteSelected} sx={{
                  fontSize: '12px', color: '#d32f2f', border: '1px solid #d32f2f',
                  borderRadius: '6px', p: '2px 12px', minWidth: 0,
                  '&:hover': { backgroundColor: '#fef2f2' },
                }}>선택 삭제</Button>
              )}
            </Box>
          </Box>

          {/* 직급 목록 (스크롤) */}
          <Box sx={{ flex: 1, overflow: 'auto', px: '28px', pb: '24px', minHeight: 0 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
                <Typography sx={{ color: '#999', fontSize: '14px' }}>로딩 중...</Typography>
              </Box>
            ) : positions.length === 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', py: 6, gap: 1 }}>
                <Typography sx={{ color: '#999', fontSize: '14px' }}>등록된 직급이 없습니다.</Typography>
                <Typography sx={{ color: '#bbb', fontSize: '12px' }}>위에서 직급명과 순서를 입력하여 추가해주세요.</Typography>
              </Box>
            ) : (
              <TableContainer sx={{ borderRadius: '8px', border: '1px solid #e8e8e8' }}>
                <Table size="small">
                  <TableHead sx={{ background: '#FAFBFC' }}>
                    <TableRow>
                      <TableCell align="center" sx={{ width: '44px', borderRight: '1px solid #e8e8e8', p: '8px' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <CustomCheckbox color="primary" name="allSelect"
                            checked={positions.length > 0 && positions.every(p => p.isChecked)}
                            onChange={handleChange} />
                        </Box>
                      </TableCell>
                      <TableCell align="center" sx={{ borderRight: '1px solid #e8e8e8', fontWeight: 600, fontSize: '13px' }}>직급명</TableCell>
                      <TableCell align="center" sx={{ borderRight: '1px solid #e8e8e8', fontWeight: 600, fontSize: '13px', width: '80px', whiteSpace: 'nowrap' }}>직급 순서</TableCell>
                      <TableCell align="center" sx={{ borderRight: '1px solid #e8e8e8', fontWeight: 600, fontSize: '13px', width: '110px' }}>등록일자</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, fontSize: '13px', width: '80px' }}>관리</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {positions.map(row => {
                      const isEditing = editingRow === row.employeePositionId;
                      return (
                        <TableRow key={row.employeePositionId} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                          <TableCell align="center" sx={{ borderRight: '1px solid #e8e8e8', p: '6px' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                              <CustomCheckbox
                                name={row.employeePositionName + row.employeePositionId}
                                checked={row?.isChecked || false}
                                onChange={handleChange} />
                            </Box>
                          </TableCell>
                          <TableCell align="center" sx={{ borderRight: '1px solid #e8e8e8', fontSize: '13px', p: '4px 8px' }}>
                            {isEditing ? (
                              <TextField size="small" value={editName} onChange={e => setEditName(e.target.value)}
                                inputProps={{ style: { fontSize: '13px', padding: '4px 8px', textAlign: 'center' } }}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '4px' } }} />
                            ) : (row.positionName || row.employeePositionName)}
                          </TableCell>
                          <TableCell align="center" sx={{ borderRight: '1px solid #e8e8e8', fontSize: '13px', p: '4px 8px' }}>
                            {isEditing ? (
                              <TextField size="small" value={editOrder}
                                onChange={e => setEditOrder(filterOrderInput(e.target.value))}
                                inputProps={{ style: { fontSize: '13px', padding: '4px 8px', textAlign: 'center', width: '40px' }, inputMode: 'numeric' }}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '4px' } }} />
                            ) : (row.order || row.employeePositionOrder)}
                          </TableCell>
                          <TableCell align="center" sx={{ borderRight: '1px solid #e8e8e8', fontSize: '13px' }}>
                            {row.registeredDate || row.createdAt || '-'}
                          </TableCell>
                          <TableCell align="center" sx={{ p: '4px' }}>
                            {isEditing ? (
                              <Box sx={{ display: 'flex', justifyContent: 'center', gap: '4px' }}>
                                <Button size="small" onClick={saveEdit} sx={{
                                  minWidth: '24px', width: '24px', height: '24px', p: 0,
                                  backgroundColor: TEAL, color: '#fff', borderRadius: '4px',
                                  '&:hover': { backgroundColor: TEAL_DARK },
                                }}><CheckIcon sx={{ fontSize: '14px' }} /></Button>
                                <Button size="small" onClick={() => setEditingRow(null)} sx={{
                                  minWidth: '24px', width: '24px', height: '24px', p: 0,
                                  backgroundColor: '#999', color: '#fff', borderRadius: '4px',
                                  '&:hover': { backgroundColor: '#777' },
                                }}><CloseRoundedIcon sx={{ fontSize: '14px' }} /></Button>
                              </Box>
                            ) : (
                              <Box sx={{ display: 'flex', justifyContent: 'center', gap: '4px' }}>
                                <Button size="small" onClick={() => startEdit(row)} sx={{
                                  minWidth: '24px', width: '24px', height: '24px', p: 0,
                                  backgroundColor: TEAL, color: '#fff', borderRadius: '4px',
                                  '&:hover': { backgroundColor: TEAL_DARK },
                                }}><EditIcon sx={{ fontSize: '14px' }} /></Button>
                                <Button size="small" onClick={() => handleDeleteSingle(row)} sx={{
                                  minWidth: '24px', width: '24px', height: '24px', p: 0,
                                  backgroundColor: '#bbb', color: '#fff', borderRadius: '4px',
                                  '&:hover': { backgroundColor: '#999' },
                                }}><DeleteOutlineIcon sx={{ fontSize: '14px' }} /></Button>
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Box>
      </Modal>

      <Modal open={confirmModal.open} onClose={() => setConfirmModal({ open: false, employees: [] })}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          width: '460px', bgcolor: '#fff', borderRadius: '16px', overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        }}>
          {/* 헤더 */}
          <Box sx={{ p: '20px 24px 16px', borderBottom: '1px solid #f0f0f0' }}>
            <Typography sx={{ fontSize: '17px', fontWeight: 700, color: '#222', textAlign: 'center' }}>
              {confirmModal.type === 'edit' ? '직급 수정' : '직급 삭제'}
            </Typography>
          </Box>

          {/* 내용 */}
          <Box sx={{ p: '20px 24px' }}>
            <Typography sx={{ fontSize: '14px', color: '#444', textAlign: 'center', mb: confirmModal.totalEmp > 0 ? 2 : 0 }}>
              {confirmModal.message}
            </Typography>

            {confirmModal.totalEmp > 0 && (
              <Box sx={{ mt: 2, p: '16px', background: '#FFF7F7', borderRadius: '12px', border: '1px solid #FFE0E0' }}>
                <Typography sx={{ fontSize: '13px', color: '#DC2626', fontWeight: 600, mb: 1, textAlign: 'center' }}>
                  소속 직원 {confirmModal.totalEmp}명의 직급이 해제됩니다
                </Typography>
                <Box sx={{
                  maxHeight: '140px', overflow: 'auto', mt: 1,
                  borderRadius: '8px', border: '1px solid #f0f0f0', background: '#fff',
                }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ background: '#FAFBFC' }}>
                        <TableCell align="center" sx={{ fontWeight: 600, fontSize: '11px', p: '6px 8px', color: '#888', borderBottom: '1px solid #f0f0f0' }}>직급</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600, fontSize: '11px', p: '6px 8px', color: '#888', borderBottom: '1px solid #f0f0f0' }}>이름</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600, fontSize: '11px', p: '6px 8px', color: '#888', borderBottom: '1px solid #f0f0f0' }}>전화번호</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600, fontSize: '11px', p: '6px 8px', color: '#888', borderBottom: '1px solid #f0f0f0' }}>입사일</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(confirmModal.employees || []).map((emp, i) => (
                        <TableRow key={i} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                          <TableCell align="center" sx={{ fontSize: '12px', p: '5px 8px', color: TEAL, fontWeight: 600 }}>{emp.positionName}</TableCell>
                          <TableCell align="center" sx={{ fontSize: '12px', p: '5px 8px', fontWeight: 500 }}>{emp.employeeName}</TableCell>
                          <TableCell align="center" sx={{ fontSize: '12px', p: '5px 8px', color: '#666' }}>{emp.phoneNumber || '-'}</TableCell>
                          <TableCell align="center" sx={{ fontSize: '12px', p: '5px 8px', color: '#666' }}>{emp.hiredDate || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Box>
            )}
          </Box>

          {/* 버튼 */}
          <Box sx={{ display: 'flex', gap: '12px', p: '0 24px 20px', justifyContent: 'center' }}>
            <Button
              onClick={() => setConfirmModal({ open: false, employees: [] })}
              sx={{
                flex: 1, py: '10px', fontSize: '14px', fontWeight: 500, borderRadius: '10px',
                color: '#666', background: '#f5f5f5', '&:hover': { background: '#eee' },
              }}>
              취소
            </Button>
            <Button
              onClick={confirmModal.onConfirm}
              sx={{
                flex: 1, py: '10px', fontSize: '14px', fontWeight: 600, borderRadius: '10px',
                color: '#fff',
                background: confirmModal.type === 'edit' ? TEAL : '#EF4444',
                '&:hover': { background: confirmModal.type === 'edit' ? TEAL_DARK : '#DC2626' },
              }}>
              {confirmModal.type === 'edit' ? '수정' : '삭제'}
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
}
